import { getBhavaForHouse } from './jyotishaBhavaHouses';
import { bhavaTatvaIdForHouse, TATVA_META } from './jyotishaTatvaCombined';
import { GRAHA_HI_EN_BY_EN } from './jyotishaGrahaNames';
import { housesOwnedByPlanet, SIGN_LORD_EN } from './jyotishaLagnaWiseAnalysis';
import { buildSummaryPhaladesh } from './jyotishaDrishtiPhaladesh';
import { houseCountOrdinalHi, houseTopicHi } from './jyotishaHouseOrdinalHi';
import { LAGNA_RASHI_OPTIONS } from './jyotishaUcchNeechCalculator';

/** Whole-sign Parashari drishti: nth house counted from planet’s bhava (planet’s house = 1st). */
export const DRISHTI_ASPECT_NUMBERS = {
  Sun: [7],
  Moon: [7],
  Mercury: [7],
  Venus: [7],
  Mars: [4, 7, 8],
  Jupiter: [5, 7, 9],
  Saturn: [3, 7, 10],
  Rahu: [5, 7, 9],
  Ketu: [5, 7, 9],
};

/** Lesson page display order: inner group, outer group, shadow grahas. */
export const DRISHTI_PAGE_GRAHA_ORDER = [
  'Sun',
  'Moon',
  'Mercury',
  'Venus',
  'Mars',
  'Jupiter',
  'Saturn',
  'Rahu',
  'Ketu',
];

export const DRISHTI_GRAHA_OPTIONS = [
  { value: 'Sun', ...GRAHA_HI_EN_BY_EN.Sun },
  { value: 'Moon', ...GRAHA_HI_EN_BY_EN.Moon },
  { value: 'Mars', ...GRAHA_HI_EN_BY_EN.Mars },
  { value: 'Mercury', ...GRAHA_HI_EN_BY_EN.Mercury },
  { value: 'Jupiter', ...GRAHA_HI_EN_BY_EN.Jupiter },
  { value: 'Venus', ...GRAHA_HI_EN_BY_EN.Venus },
  { value: 'Saturn', ...GRAHA_HI_EN_BY_EN.Saturn },
  { value: 'Rahu', ...GRAHA_HI_EN_BY_EN.Rahu },
  { value: 'Ketu', ...GRAHA_HI_EN_BY_EN.Ketu },
];

/** @param {string} grahaEn */
export function drishtiAspectPatternForGraha(grahaEn) {
  return DRISHTI_ASPECT_NUMBERS[grahaEn] ?? [];
}

/** Graha options in lesson-page order (calculator dropdowns keep DRISHTI_GRAHA_OPTIONS). */
export const DRISHTI_PAGE_GRAHA_OPTIONS = DRISHTI_PAGE_GRAHA_ORDER.map((value) =>
  DRISHTI_GRAHA_OPTIONS.find((g) => g.value === value),
).filter(Boolean);

/** Natural friendship (Parashari) for yuti tone - same table as Panchadha lesson. */
const NATURAL_REL = {
  Sun: { Moon: 'friend', Mars: 'friend', Jupiter: 'friend', Mercury: 'neutral', Venus: 'enemy', Saturn: 'enemy' },
  Moon: { Sun: 'friend', Mercury: 'friend', Mars: 'neutral', Jupiter: 'neutral', Venus: 'neutral', Saturn: 'neutral' },
  Mars: { Sun: 'friend', Moon: 'friend', Jupiter: 'friend', Mercury: 'enemy', Venus: 'neutral', Saturn: 'neutral' },
  Mercury: { Sun: 'friend', Venus: 'friend', Moon: 'enemy', Mars: 'neutral', Jupiter: 'neutral', Saturn: 'neutral' },
  Jupiter: { Sun: 'friend', Moon: 'friend', Mars: 'friend', Mercury: 'enemy', Venus: 'enemy', Saturn: 'neutral' },
  Venus: { Mercury: 'friend', Saturn: 'friend', Sun: 'enemy', Moon: 'enemy', Mars: 'neutral', Jupiter: 'neutral' },
  Saturn: { Mercury: 'friend', Venus: 'friend', Sun: 'enemy', Moon: 'enemy', Mars: 'neutral', Jupiter: 'neutral' },
};

/** Well-known yuti patterns referenced in Takshify lessons / dosha notes. */
const YUTI_NOTABLE = [
  {
    planets: ['Sun', 'Rahu'],
    hi: 'सूर्य-राहु युति: ग्रहण जैसी तीव्रता; अहंकार, पिता/अधिकारी विषय में उतार-चढ़ाव संभव।',
    en: 'Sun–Rahu: eclipse-like intensity; ego and authority themes may swing sharply.',
  },
  {
    planets: ['Sun', 'Ketu'],
    hi: 'सूर्य-केतु युति: आत्म-विश्लेषण, आध्यात्म या अलगाव की झुकाव; पिता/सत्ता विषय में रहस्य।',
    en: 'Sun–Ketu: introspection, spiritual or detached tone around self and father figures.',
  },
  {
    planets: ['Moon', 'Rahu'],
    hi: 'चंद्र-राहु (ग्रहण): मन अधिक सक्रिय/अशांत; भावनात्मक उतार-चढ़ाव - पूर्ण फल दशा व दृष्टि से।',
    en: 'Moon–Rahu: restless mind, emotional swings - final result needs dasha and aspects.',
  },
  {
    planets: ['Moon', 'Saturn'],
    hi: 'चंद्र-शनि: मन पर दबाव, गंभीरता या विलंब; भावनात्मक संयम या उदासी की संभावना।',
    en: 'Moon–Saturn: mental pressure, seriousness, delay, or emotional restraint.',
  },
  {
    planets: ['Mars', 'Rahu'],
    hi: 'मंगल-राहु (अंगारक संकेत): तीव्र ऊर्जा, साहस या विवाद - नियंत्रण और दशा महत्वपूर्ण।',
    en: 'Mars–Rahu (Angarak hint): sharp energy, courage or conflict - needs balance and timing.',
  },
  {
    planets: ['Mars', 'Saturn'],
    hi: 'मंगल-शनि: परिश्रम, रुकावट के बाद कार्य; क्रोध-धैर्य का मिश्रण संभव।',
    en: 'Mars–Saturn: effort through obstacles; blend of drive and restraint.',
  },
  {
    planets: ['Jupiter', 'Rahu'],
    hi: 'गुरु-राहु (गुरु चांडाल संकेत): धर्म/गुरु विषय में अनूठा या विवादास्पद रास्ता।',
    en: 'Jupiter–Rahu: unconventional or debated path in belief, teachers, or wisdom.',
  },
  {
    planets: ['Venus', 'Saturn'],
    hi: 'शुक्र-शनि: प्रेम/विलास में गंभीरता या विलंब; दीर्घकालिक संबंध की झुकाव।',
    en: 'Venus–Saturn: seriousness or delay in love and comforts; long-term bond tendency.',
  },
  {
    planets: ['Venus', 'Rahu'],
    hi: 'शुक्र-राहु: विलास/कला में आकर्षण अधिक; संबंधों में तीव्र इच्छा या असामान्यता।',
    en: 'Venus–Rahu: strong pull toward pleasure, art, or unusual relationship patterns.',
  },
];

function naturalRel(a, b) {
  if (a === b) return 'same';
  return NATURAL_REL[a]?.[b] ?? NATURAL_REL[b]?.[a] ?? 'neutral';
}

function relLabel(rel) {
  if (rel === 'friend') return { hi: 'मित्र', en: 'friend' };
  if (rel === 'enemy') return { hi: 'शत्रु', en: 'enemy' };
  return { hi: 'सम', en: 'neutral' };
}

/**
 * Yuti (conjunction) reading for grahas in the same house - aligned with course fixed-rules + Grah Yuddha lesson.
 * @param {string[]} grahas
 * @param {number} [house] 1–12 (short label only; full yuti phaladesh is in Summary)
 */
export function analyzeYuti(grahas, house) {
  const list = [...grahas];
  const h = house ?? 1;

  if (!list.length) {
    return {
      hasYuti: false,
      count: 0,
      tone: 'empty',
      summaryHi: 'कोई ग्रह नहीं - युति नहीं।',
      summaryEn: 'No graha placed - no yuti.',
      pairs: [],
      notable: [],
    };
  }
  if (list.length === 1) {
    return {
      hasYuti: false,
      count: 1,
      tone: 'single',
      summaryHi: 'एक ग्रह - युति नहीं; विस्तृत फल संक्षिप्त फल में।',
      summaryEn: 'Single graha - no yuti; see Summary · Phaladesh below.',
      pairs: [],
      notable: [],
    };
  }

  /** @type {Array<{ a: string; b: string; rel: string; relHi: string; relEn: string }>} */
  const pairs = [];
  let friendCount = 0;
  let enemyCount = 0;
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const rel = naturalRel(list[i], list[j]);
      const lbl = relLabel(rel);
      if (rel === 'friend') friendCount += 1;
      else if (rel === 'enemy') enemyCount += 1;
      pairs.push({ a: list[i], b: list[j], rel, relHi: lbl.hi, relEn: lbl.en });
    }
  }

  const notable = YUTI_NOTABLE.filter((n) => n.planets.every((p) => list.includes(p)));

  let tone = 'mixed';
  if (enemyCount > friendCount) tone = 'tense';
  else if (friendCount > enemyCount) tone = 'harmonious';

  const summaryHi =
    list.length >= 3
      ? `${houseTopicHi(h)} में ${list.length} ग्रहों की युति - प्राकृतिक मित्र/शत्रु जोड़े नीचे; विस्तृत फल संक्षिप्त फल · फल-संकेत में।`
      : `${houseTopicHi(h)} में दो ग्रहों की युति - विस्तृत फल संक्षिप्त फल · फल-संकेत में।`;
  const summaryEn =
    list.length >= 3
      ? `${list.length} grahas in yuti in ${houseTopicHi(h)} - natural friend/enemy pairs below; full reading in Summary · Phaladesh.`
      : `Two-graha yuti in ${houseTopicHi(h)} - full reading in Summary · Phaladesh.`;

  return {
    hasYuti: true,
    count: list.length,
    tone,
    summaryHi,
    summaryEn,
    pairs,
    notable,
  };
}

/**
 * @param {number} fromHouse 1-12
 * @param {number} aspectHouseNumber e.g. 7 for 7th drishti
 */
export function drishtiTargetHouse(fromHouse, aspectHouseNumber) {
  return ((fromHouse - 1 + (aspectHouseNumber - 1)) % 12) + 1;
}

/**
 * @param {string} grahaEn
 * @param {number} fromHouse
 * @returns {Array<{ targetHouse: number; aspectLabel: string }>}
 */
export function grahaDrishtiFromHouse(grahaEn, fromHouse) {
  const nums = DRISHTI_ASPECT_NUMBERS[grahaEn];
  if (!nums?.length) return [];
  return nums.map((n) => ({
    targetHouse: drishtiTargetHouse(fromHouse, n),
    aspectLabel: `${n}th`,
  }));
}

/**
 * @param {number} lagnaSign 1-12
 * @param {number} house 1-12
 */
export function signInHouse(lagnaSign, house) {
  return ((lagnaSign - 1 + house - 1) % 12) + 1;
}

/**
 * @param {number} lagnaSign
 * @param {number} house
 */
export function lordOfHouse(lagnaSign, house) {
  const signIdx = signInHouse(lagnaSign, house) - 1;
  return SIGN_LORD_EN[signIdx];
}

function grahaLabel(grahaEn) {
  const g = GRAHA_HI_EN_BY_EN[grahaEn];
  return g ? `${g.hi} (${g.en})` : grahaEn;
}

function houseBhavaMeta(house) {
  return getBhavaForHouse(house);
}

/**
 * @param {Record<number, string[]>} placements house 1-12 → graha English names
 * @param {number} lagnaSign 1-12
 */
export function evaluateDrishtiChart(placements, lagnaSign) {
  if (!lagnaSign || lagnaSign < 1 || lagnaSign > 12) {
    return { ok: false, error: 'Select Lagna sign (1–12).' };
  }

  const lagnaIndex = lagnaSign - 1;
  const hasAnyGraha = Object.values(placements).some((arr) => arr?.length > 0);
  if (!hasAnyGraha) {
    return { ok: false, error: 'Place at least one graha in a house.' };
  }

  /** @type {Array<{ house: number; sign: number; signHi: string; signEn: string; grahas: string[]; lord: string }>} */
  const houseRows = [];
  for (let h = 1; h <= 12; h++) {
    const sign = signInHouse(lagnaSign, h);
    const rashi = LAGNA_RASHI_OPTIONS.find((r) => r.value === sign);
    const bhava = getBhavaForHouse(h);
    const bhavaTatvaId = bhavaTatvaIdForHouse(h);
    const bhavaTatva = TATVA_META[bhavaTatvaId];
    houseRows.push({
      house: h,
      sign,
      signHi: rashi?.hi ?? '',
      signEn: rashi?.en ?? '',
      grahas: [...(placements[h] || [])],
      lord: lordOfHouse(lagnaSign, h),
      karakHi: bhava?.karakHi ?? '',
      karakEn: bhava?.karakEn ?? '',
      bhavaTatvaId,
      bhavaTatvaHi: bhavaTatva?.hi ?? '',
      bhavaTatvaEn: bhavaTatva?.en ?? '',
    });
  }

  /** @type {Array<{ graha: string; fromHouse: number; aspects: Array<{ targetHouse: number; aspectLabel: string }> }>} */
  const grahaDrishti = [];
  for (let fromHouse = 1; fromHouse <= 12; fromHouse++) {
    for (const graha of placements[fromHouse] || []) {
      const aspects = grahaDrishtiFromHouse(graha, fromHouse);
      if (aspects.length) {
        grahaDrishti.push({ graha, fromHouse, aspects });
      }
    }
  }

  /** Bhavesh: lord of house H sits somewhere; aspects from placement house */
  /** @type {Array<{ ownedHouse: number; lord: string; lordPlacedIn: number | null; aspects: Array<{ targetHouse: number; aspectLabel: string }>; ownsAlso: number[] }>} */
  const bhaveshDrishti = [];
  for (let ownedHouse = 1; ownedHouse <= 12; ownedHouse++) {
    const lord = lordOfHouse(lagnaSign, ownedHouse);
    let lordPlacedIn = null;
    for (let h = 1; h <= 12; h++) {
      if ((placements[h] || []).includes(lord)) {
        lordPlacedIn = h;
        break;
      }
    }
    const ownsAlso = housesOwnedByPlanet(lagnaIndex, lord).filter((h) => h !== ownedHouse);
    const aspects = lordPlacedIn != null ? grahaDrishtiFromHouse(lord, lordPlacedIn) : [];
    bhaveshDrishti.push({
      ownedHouse,
      lord,
      lordPlacedIn,
      ownsAlso,
      aspects,
    });
  }

  /** Inbound drishti per house */
  /** @type {Record<number, { fromGrahas: Array<{ graha: string; fromHouse: number; aspectLabel: string; kind: 'placement' }>; fromLords: Array<{ lord: string; ownedHouse: number; fromHouse: number; aspectLabel: string; kind: 'bhavesh' }> }>} */
  const inbound = {};
  for (let h = 1; h <= 12; h++) {
    inbound[h] = { fromGrahas: [], fromLords: [] };
  }

  for (const row of grahaDrishti) {
    for (const a of row.aspects) {
      inbound[a.targetHouse].fromGrahas.push({
        graha: row.graha,
        fromHouse: row.fromHouse,
        aspectLabel: a.aspectLabel,
        kind: 'placement',
      });
    }
  }

  for (const row of bhaveshDrishti) {
    if (row.lordPlacedIn == null) continue;
    for (const a of row.aspects) {
      inbound[a.targetHouse].fromLords.push({
        lord: row.lord,
        ownedHouse: row.ownedHouse,
        fromHouse: row.lordPlacedIn,
        aspectLabel: a.aspectLabel,
        kind: 'bhavesh',
      });
    }
  }

  /** @type {Array<{ house: number; occupants: string[]; inbound: typeof inbound[1]; summaryHouseHi: string; summaryOccupantsHi: string; summaryDrishtiHi: string | null; summaryToneHi: string | null; summaryHouseEn: string; summaryOccupantsEn: string; summaryDrishtiEn: string | null; summaryToneEn: string | null; effectHi: string; effectEn: string }>} */
  const houseEffects = [];
  for (let h = 1; h <= 12; h++) {
    const meta = houseBhavaMeta(h);
    const occupants = placements[h] || [];
    const inc = inbound[h];
    const aspectors = [...inc.fromGrahas, ...inc.fromLords];

    const summaryHouseHi = meta
      ? `यह ${houseTopicHi(h)} (${meta.significanceHi})${meta.karakHi ? ` - कारक ग्रह: ${meta.karakHi}` : ''}`
      : `यह ${houseTopicHi(h)}`;

    const summaryHouseEn = meta
      ? `House ${h} (${meta.significanceEn})${meta.karakEn ? ` - Kaarak gruh: ${meta.karakEn}` : ''}`
      : `House ${h}`;

    const summaryOccupantsHi = occupants.length
      ? `यहाँ बैठे: ${occupants.map(grahaLabel).join(', ')}`
      : 'यहाँ बैठे: कोई नहीं (खाली भाव)';

    const summaryOccupantsEn = occupants.length
      ? `Placed here: ${occupants.map(grahaLabel).join(', ')}`
      : 'Placed here: none (empty house)';

    let summaryDrishtiHi = null;
    /** @type {string[] | null} */
    let summaryDrishtiPartsHi = null;
    let summaryDrishtiEn = null;
    /** @type {string[] | null} */
    let summaryDrishtiPartsEn = null;
    let summaryToneHi = null;
    let summaryToneEn = null;

    if (!aspectors.length) {
      summaryDrishtiHi = 'दृष्टि: किसी ग्रह/भावेश की शास्त्रीय दृष्टि नहीं';
      summaryDrishtiEn = 'Drishti: no classical graha or bhavesh aspect';
    } else {
      summaryDrishtiPartsHi = aspectors.map((x) => {
        if (x.kind === 'placement') {
          return `${grahaLabel(x.graha)} (${houseCountOrdinalHi(x.fromHouse)} से ${x.aspectLabel} दृष्टि)`;
        }
        return `${houseCountOrdinalHi(x.ownedHouse)} के स्वामी ${grahaLabel(x.lord)} (${houseCountOrdinalHi(x.fromHouse)} बैठे, ${x.aspectLabel} दृष्टि)`;
      });
      summaryDrishtiPartsEn = aspectors.map((x) => {
        if (x.kind === 'placement') {
          return `${grahaLabel(x.graha)} (${x.aspectLabel} from ${houseTopicHi(x.fromHouse)})`;
        }
        return `lord of ${houseTopicHi(x.ownedHouse)} ${grahaLabel(x.lord)} (in ${houseTopicHi(x.fromHouse)}, ${x.aspectLabel})`;
      });

    }

    const lord = lordOfHouse(lagnaSign, h);
    const summaryPhaladesh = buildSummaryPhaladesh(h, occupants, aspectors, lord, meta);
    const summaryPhalaSections = summaryPhaladesh.sections;
    const summaryPhalaLinesHi = summaryPhaladesh.linesHi;
    const summaryPhalaLinesEn = summaryPhaladesh.linesEn;
    summaryToneHi = null;
    summaryToneEn = null;

    const drishtiHiBlock = summaryDrishtiPartsHi?.length
      ? ['दृष्टि:', ...summaryDrishtiPartsHi].join('\n')
      : summaryDrishtiHi;
    const drishtiEnBlock = summaryDrishtiPartsEn?.length
      ? ['Drishti:', ...summaryDrishtiPartsEn].join('\n')
      : summaryDrishtiEn;

    const phalaHiBlock = summaryPhalaLinesHi.length ? ['फल-संकेत:', ...summaryPhalaLinesHi].join('\n') : '';
    const phalaEnBlock = summaryPhalaLinesEn.length ? ['Phaladesh:', ...summaryPhalaLinesEn].join('\n') : '';

    const effectHi = [summaryHouseHi, summaryOccupantsHi, drishtiHiBlock, phalaHiBlock].filter(Boolean).join('\n');
    const effectEn = [summaryHouseEn, summaryOccupantsEn, drishtiEnBlock, phalaEnBlock].filter(Boolean).join('\n');

    houseEffects.push({
      house: h,
      occupants,
      inbound: inc,
      summaryHouseHi,
      summaryOccupantsHi,
      summaryDrishtiHi,
      summaryDrishtiPartsHi,
      summaryPhalaSections,
      summaryPhalaLinesHi,
      summaryToneHi,
      summaryHouseEn,
      summaryOccupantsEn,
      summaryDrishtiEn,
      summaryDrishtiPartsEn,
      summaryPhalaLinesEn,
      summaryToneEn,
      effectHi,
      effectEn,
    });
  }

  /** Structured per-house panel data for compact UI */
  const houseDetails = houseRows.map((row) => {
    const he = houseEffects.find((x) => x.house === row.house);
    const meta = houseBhavaMeta(row.house);
    const yuti = analyzeYuti(row.grahas, row.house);
    const outboundFromPlacement = grahaDrishti.filter((g) => g.fromHouse === row.house);
    const bhaveshOfHouse = bhaveshDrishti.find((b) => b.ownedHouse === row.house);
    const inboundCount = (he?.inbound.fromGrahas.length ?? 0) + (he?.inbound.fromLords.length ?? 0);

    return {
      ...row,
      meta,
      yuti,
      inbound: he?.inbound ?? { fromGrahas: [], fromLords: [] },
      inboundCount,
      outboundFromPlacement,
      bhaveshOfHouse,
      summaryHouseHi: he?.summaryHouseHi ?? '',
      summaryOccupantsHi: he?.summaryOccupantsHi ?? '',
      summaryDrishtiHi: he?.summaryDrishtiHi ?? null,
      summaryDrishtiPartsHi: he?.summaryDrishtiPartsHi ?? null,
      summaryPhalaSections: he?.summaryPhalaSections ?? [],
      summaryPhalaLinesHi: he?.summaryPhalaLinesHi ?? [],
      summaryToneHi: he?.summaryToneHi ?? null,
      summaryHouseEn: he?.summaryHouseEn ?? '',
      summaryOccupantsEn: he?.summaryOccupantsEn ?? '',
      summaryDrishtiEn: he?.summaryDrishtiEn ?? null,
      summaryDrishtiPartsEn: he?.summaryDrishtiPartsEn ?? null,
      summaryPhalaLinesEn: he?.summaryPhalaLinesEn ?? [],
      summaryToneEn: he?.summaryToneEn ?? null,
      effectHi: he?.effectHi ?? '',
      effectEn: he?.effectEn ?? '',
    };
  });

  return {
    ok: true,
    lagnaSign,
    lagnaRashi: LAGNA_RASHI_OPTIONS.find((r) => r.value === lagnaSign),
    houseRows,
    houseDetails,
    grahaDrishti,
    bhaveshDrishti,
    houseEffects,
  };
}
